/**
 * 笔记详情弹窗组件
 * 小红书风格设计，支持桌面端和移动端
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuthStore } from '../../stores/authStore';
import { getPostDetail, checkPostLikeStatus, togglePostLike, deletePost } from '../../services/postService';
import { getPostComments, getCommentReplies, createComment, toggleCommentLike, getCommentsLikeStatus, type CommentDetail } from '../../services/commentService';
import { checkFollowStatus, toggleFollow } from '../../services/followService';
import Avatar from '../common/Avatar';
import ConfirmModal from '../common/ConfirmModal';
import type { PostDetail } from '../../services/postService';

interface NoteDetailModalProps {
  postId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onDeleteSuccess?: () => void; // 删除成功回调
  // 新增：定位和高亮评论
  highlightCommentId?: string;    // 要定位高亮的评论ID
  expandParentId?: string;        // 需要自动展开的父评论ID
}

// 格式化数字
const formatCount = (count: number): string => {
  if (count >= 10000) return `${(count / 10000).toFixed(1)}w`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return count.toString();
};

// 格式化时间
const formatTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours === 0) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes}m ago`;
    }
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return `${date.getMonth() + 1}-${date.getDate()}`;
  }
};

// 帖子详情骨架屏组件
const NoteDetailSkeleton: React.FC = () => (
  <div className="note-modal-skeleton skeleton">
    {/* 作者区域骨架屏 */}
    <div className="skeleton-note-author">
      <div className="skeleton-note-author-avatar"></div>
      <div className="skeleton-note-author-name"></div>
    </div>
    {/* 内容区域骨架屏 */}
    <div className="skeleton-note-content">
      <div className="skeleton-note-line"></div>
      <div className="skeleton-note-line"></div>
      <div className="skeleton-note-line-short"></div>
      {/* 标签骨架屏 */}
      <div className="skeleton-note-tags">
        <div className="skeleton-note-tag"></div>
        <div className="skeleton-note-tag"></div>
      </div>
      {/* 评论标题骨架屏 */}
      <div className="skeleton-comments-header"></div>
      {/* 评论骨架屏 */}
      {[1, 2, 3].map(i => (
        <div key={i} className="skeleton-comment-item">
          <div className="skeleton-comment-avatar"></div>
          <div className="skeleton-comment-content">
            <div className="skeleton-comment-name"></div>
            <div className="skeleton-comment-text"></div>
            <div className="skeleton-comment-meta">
              <div className="skeleton-comment-time"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// 图片区域骨架屏
const ImageSkeleton: React.FC = () => (
  <div className="skeleton-image-area skeleton">
    <svg className="w-16 h-16 text-[#D0D0D0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  </div>
);

const NoteDetailModal: React.FC<NoteDetailModalProps> = ({
  postId,
  isOpen,
  onClose,
  onDeleteSuccess,
  highlightCommentId,
  expandParentId
}) => {
  const { user, isLoggedIn, openLoginModal } = useAuthStore();
  const queryClient = useQueryClient();

  // 图片轮播状态
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // 评论输入状态
  const [commentInput, setCommentInput] = useState('');
  const [replyingTo, setReplyingTo] = useState<CommentDetail | null>(null);
  const [replyingToParentId, setReplyingToParentId] = useState<string | null>(null); // 一级评论ID（parent_id）
  const [replyingToId, setReplyingToId] = useState<string | null>(null); // 具体回复的评论ID（reply_to_id）
  const [submitting, setSubmitting] = useState(false);

  // 展开的评论ID列表，用于获取子评论的点赞状态
  const [expandedCommentIds, setExpandedCommentIds] = useState<Set<string>>(new Set());

  // 删除确认弹窗状态
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 评论高亮状态
  const [highlightedCommentId, setHighlightedCommentId] = useState<string | null>(null);
  const highlightProcessedRef = useRef(false);

  // 自动展开父评论
  useEffect(() => {
    if (isOpen && expandParentId && !highlightProcessedRef.current) {
      setExpandedCommentIds(prev => {
        const next = new Set(prev);
        next.add(expandParentId);
        return next;
      });
    }
  }, [isOpen, expandParentId]);

  // 自动定位和高亮评论
  useEffect(() => {
    if (!isOpen || !highlightCommentId || highlightProcessedRef.current) return;

    // 等待评论列表渲染完成
    const scrollToComment = () => {
      const commentElement = document.querySelector(`[data-comment-id="${highlightCommentId}"]`);
      if (commentElement) {
        // 滚动到评论位置
        commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // 设置高亮状态
        setHighlightedCommentId(highlightCommentId);
        // 标记已处理
        highlightProcessedRef.current = true;
        // 3秒后清除高亮
        setTimeout(() => {
          setHighlightedCommentId(null);
        }, 3000);
      }
    };

    // 尝试立即滚动，如果元素不存在则延迟尝试
    scrollToComment();
    const timer = setTimeout(scrollToComment, 500);
    return () => clearTimeout(timer);
  }, [isOpen, highlightCommentId]);

  // 弹窗关闭时清除高亮状态
  useEffect(() => {
    if (!isOpen) {
      setHighlightedCommentId(null);
      highlightProcessedRef.current = false;
    }
  }, [isOpen]);

  // 获取帖子详情 - 添加轮询机制实现实时刷新（每5秒刷新一次）
  const { data: post, isLoading: postLoading } = useQuery({
    queryKey: ['postDetail', postId],
    queryFn: () => getPostDetail(postId!),
    enabled: !!postId && isOpen,
    refetchInterval: isOpen ? 5000 : false, // 弹窗打开时每5秒刷新帖子详情
  });

  // 获取评论列表 - 添加轮询机制实现实时刷新（每3秒刷新一次）
  const { data: comments = [], isLoading: commentsLoading } = useQuery({
    queryKey: ['postComments', postId],
    queryFn: () => getPostComments(postId!),
    enabled: !!postId && isOpen,
    refetchInterval: isOpen ? 3000 : false, // 弹窗打开时每3秒刷新评论
  });

  // 获取帖子点赞状态
  const { data: isPostLiked = false } = useQuery({
    queryKey: ['postLikeStatus', postId, user?.id],
    queryFn: () => checkPostLikeStatus(postId!, user!.id),
    enabled: !!postId && !!user && isOpen,
  });

  // 获取关注状态
  const { data: isFollowing = false } = useQuery({
    queryKey: ['followStatus', user?.id, post?.author_id, post?.author_type],
    queryFn: () => checkFollowStatus(user!.id, post!.author_id, post!.author_type),
    enabled: !!user && !!post && isOpen,
  });

  // 获取评论点赞状态
  const { data: commentLikeStatuses = {} } = useQuery({
    queryKey: ['commentLikeStatuses', postId, user?.id],
    queryFn: () => getCommentsLikeStatus(comments.map(c => c.id), user!.id),
    enabled: !!user && comments.length > 0 && isOpen,
  });

  // 获取所有展开评论的回复
  const allRepliesQuery = useQuery({
    queryKey: ['allExpandedReplies', postId, Array.from(expandedCommentIds)],
    queryFn: async () => {
      const repliesMap: Record<string, CommentDetail[]> = {};
      for (const commentId of expandedCommentIds) {
        const replies = await getCommentReplies(commentId);
        repliesMap[commentId] = replies;
      }
      return repliesMap;
    },
    enabled: expandedCommentIds.size > 0 && isOpen,
    refetchInterval: isOpen && expandedCommentIds.size > 0 ? 3000 : false, // 每3秒刷新展开的回复
  });

  // 获取所有展开回复的点赞状态
  const allReplyIds = Object.values(allRepliesQuery.data || {}).flat().map(r => r.id);
  const { data: replyLikeStatuses = {} } = useQuery({
    queryKey: ['replyLikeStatuses', postId, user?.id, allReplyIds],
    queryFn: () => getCommentsLikeStatus(allReplyIds, user!.id),
    enabled: !!user && allReplyIds.length > 0 && isOpen,
  });

  // 点赞帖子 - 使用乐观更新
  const likeMutation = useMutation({
    onMutate: (currentLiked: boolean) => {
      // 乐观更新：立即更新 UI
      queryClient.setQueryData(['postLikeStatus', postId, user?.id], !currentLiked);
      queryClient.setQueryData(['postDetail', postId], (old: PostDetail | undefined) => {
        if (old) return { ...old, likes_count: Math.max(0, old.likes_count + (currentLiked ? -1 : 1)) };
        return old;
      });
      return { currentLiked };
    },
    mutationFn: (currentLiked: boolean) => togglePostLike(postId!, user!.id, currentLiked),
    onSuccess: (data) => {
      if (data) {
        // 智能验证：如果 isLiked=true 但 likesCount<=0，可能是数据库延迟，保留乐观值
        const shouldUseServerValue = !(
          data.isLiked === true && data.likesCount <= 0
        );

        // 更新点赞状态（始终使用服务器返回值）
        queryClient.setQueryData(['postLikeStatus', postId, user?.id], data.isLiked);

        // 更新点赞数（如果服务器值合理则使用）
        if (shouldUseServerValue) {
          queryClient.setQueryData(['postDetail', postId], (old: PostDetail | undefined) => {
            if (old) return { ...old, likes_count: data.likesCount };
            return old;
          });
        }
      }
    },
    onError: (error, _currentLiked, context) => {
      // 错误时回滚乐观更新
      if (context?.currentLiked !== undefined) {
        queryClient.setQueryData(['postLikeStatus', postId, user?.id], context.currentLiked);
        queryClient.setQueryData(['postDetail', postId], (old: PostDetail | undefined) => {
          if (old) return { ...old, likes_count: Math.max(0, old.likes_count - (context.currentLiked ? -1 : 1)) };
          return old;
        });
      }
      console.error('帖子点赞失败:', error);
    },
  });

  // 关注作者
  const followMutation = useMutation({
    mutationFn: () => toggleFollow(user!.id, post!.author_id, post!.author_type, isFollowing),
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(['followStatus', user?.id, post?.author_id, post?.author_type], data.isFollowing);
      }
    },
  });

  // 点赞评论（同时支持主评论和子评论）- 使用乐观更新
  const commentLikeMutation = useMutation({
    onMutate: ({ commentId, currentLiked }: { commentId: string; currentLiked: boolean }) => {
      // 乐观更新：立即更新 UI
      // 1. 更新点赞状态缓存
      queryClient.setQueryData(['commentLikeStatuses', postId, user?.id], (old: Record<string, boolean> = {}) => ({
        ...old,
        [commentId]: !currentLiked,
      }));
      queryClient.setQueryData(['replyLikeStatuses', postId, user?.id], (old: Record<string, boolean> = {}) => ({
        ...old,
        [commentId]: !currentLiked,
      }));

      // 2. 更新点赞数（乐观增加或减少）
      queryClient.setQueryData(['postComments', postId], (old: CommentDetail[] = []) =>
        old.map(c => c.id === commentId ? { ...c, likes_count: Math.max(0, Number(c.likes_count) + (currentLiked ? -1 : 1)) } : c)
      );
      queryClient.setQueryData(['allExpandedReplies', postId], (old: Record<string, CommentDetail[]> = {}) => {
        const updated = { ...old };
        for (const commentIdKey in updated) {
          updated[commentIdKey] = updated[commentIdKey].map(r =>
            r.id === commentId ? { ...r, likes_count: Math.max(0, Number(r.likes_count) + (currentLiked ? -1 : 1)) } : r
          );
        }
        return updated;
      });

      return { currentLiked };
    },
    mutationFn: ({ commentId, currentLiked }: { commentId: string; currentLiked: boolean }) => {
      return toggleCommentLike(commentId, user!.id, currentLiked);
    },
    onSuccess: (data, variables) => {
      const { commentId, currentLiked } = variables;
      // 如果服务器返回了有效数据，更新缓存
      if (data) {
        // 智能验证：如果 isLiked=true 但 likesCount<=0，可能是数据库延迟，保留乐观值
        const shouldUseServerValue = !(
          data.isLiked === true && data.likesCount <= 0
        ) && !(
          data.isLiked === false && data.likesCount > 0 && currentLiked === false
        );

        // 更新点赞状态
        queryClient.setQueryData(['commentLikeStatuses', postId, user?.id], (old: Record<string, boolean> = {}) => ({
          ...old,
          [commentId]: data.isLiked,
        }));
        queryClient.setQueryData(['replyLikeStatuses', postId, user?.id], (old: Record<string, boolean> = {}) => ({
          ...old,
          [commentId]: data.isLiked,
        }));

        // 更新点赞数（如果服务器值合理则使用，否则保留乐观值）
        if (shouldUseServerValue) {
          queryClient.setQueryData(['postComments', postId], (old: CommentDetail[] = []) =>
            old.map(c => c.id === commentId ? { ...c, likes_count: data.likesCount } : c)
          );
          queryClient.setQueryData(['allExpandedReplies', postId], (old: Record<string, CommentDetail[]> = {}) => {
            const updated = { ...old };
            for (const commentIdKey in updated) {
              updated[commentIdKey] = updated[commentIdKey].map(r =>
                r.id === commentId ? { ...r, likes_count: data.likesCount } : r
              );
            }
            return updated;
          });
        }
      }
    },
    onError: (error, variables, context) => {
      const { commentId } = variables;
      // 错误时回滚乐观更新
      if (context?.currentLiked !== undefined) {
        queryClient.setQueryData(['commentLikeStatuses', postId, user?.id], (old: Record<string, boolean> = {}) => ({
          ...old,
          [commentId]: context.currentLiked,
        }));
        queryClient.setQueryData(['replyLikeStatuses', postId, user?.id], (old: Record<string, boolean> = {}) => ({
          ...old,
          [commentId]: context.currentLiked,
        }));
        queryClient.setQueryData(['postComments', postId], (old: CommentDetail[] = []) =>
          old.map(c => c.id === commentId ? { ...c, likes_count: Math.max(0, Number(c.likes_count) - (context.currentLiked ? -1 : 1)) } : c)
        );
        queryClient.setQueryData(['allExpandedReplies', postId], (old: Record<string, CommentDetail[]> = {}) => {
          const updated = { ...old };
          for (const commentIdKey in updated) {
            updated[commentIdKey] = updated[commentIdKey].map(r =>
              r.id === commentId ? { ...r, likes_count: Math.max(0, Number(r.likes_count) - (context.currentLiked ? -1 : 1)) } : r
            );
          }
          return updated;
        });
      }
      console.error('评论点赞失败:', error);
    },
  });

  // 发表评论
  const handleSubmitComment = useCallback(async () => {
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }

    if (!commentInput.trim() || submitting) return;

    setSubmitting(true);
    try {
      // parent_id: 一级评论ID（所有回复都挂在一级评论下，保持扁平结构）
      // 如果回复子评论，使用 replyingToParentId；如果回复一级评论，使用 replyingTo?.id
      const parentId = replyingToParentId || replyingTo?.id;
      // reply_to_id: 具体回复的评论ID（用于区分回复目标）
      // 如果回复子评论，使用 replyingToId；如果回复一级评论，使用 replyingTo?.id
      const replyToId = replyingToId || replyingTo?.id;

      const newComment = await createComment(
        postId!,
        user!.id,
        commentInput.trim(),
        parentId,
        replyToId  // 新增参数
      );

      if (newComment) {
        setCommentInput('');
        setReplyingTo(null);
        setReplyingToParentId(null);
        setReplyingToId(null);  // 清除 replyToId

        // 发送评论成功后立即刷新数据，让用户看到自己的评论
        // 同时也给 agent 即时回复的机会
        queryClient.invalidateQueries({ queryKey: ['postComments', postId] });
        queryClient.invalidateQueries({ queryKey: ['postDetail', postId] });
        // 同时刷新回复列表和点赞状态
        queryClient.invalidateQueries({ queryKey: ['allExpandedReplies', postId] });
        queryClient.invalidateQueries({ queryKey: ['replyLikeStatuses', postId] });
      }
    } finally {
      setSubmitting(false);
    }
  }, [commentInput, submitting, postId, user, isLoggedIn, replyingTo, replyingToParentId, replyingToId, openLoginModal, queryClient]);

  // 处理点赞
  const handleLikePost = useCallback(() => {
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }
    // 传递当前点赞状态（避免闭包问题）
    likeMutation.mutate(isPostLiked);
  }, [isLoggedIn, openLoginModal, likeMutation, isPostLiked]);

  // 处理关注
  const handleFollow = useCallback(() => {
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }
    followMutation.mutate();
  }, [isLoggedIn, openLoginModal, followMutation]);

  // 处理评论点赞
  const handleLikeComment = useCallback((commentId: string) => {
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }
    // 从缓存中获取当前状态（确保使用正确的值）
    const currentLiked = commentLikeStatuses[commentId] || false;
    commentLikeMutation.mutate({ commentId, currentLiked });
  }, [isLoggedIn, openLoginModal, commentLikeMutation, commentLikeStatuses]);

  // 处理回复（回复一级评论）
  const handleReply = useCallback((comment: CommentDetail) => {
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }
    setReplyingTo(comment);
    setReplyingToParentId(null);  // 回复一级评论，parent_id 为 null
    setReplyingToId(null);        // 回复一级评论，reply_to_id 也为 null（一级评论不设置 reply_to_id）
    setCommentInput(`@${comment.author_name} `);
  }, [isLoggedIn, openLoginModal]);

  // 处理回复子评论
  const handleReplyToReply = useCallback((reply: CommentDetail, parentComment: CommentDetail) => {
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }
    setReplyingTo(reply);
    setReplyingToParentId(parentComment.id); // 一级评论ID（parent_id）
    setReplyingToId(reply.id);               // 具体回复的评论ID（reply_to_id）
    setCommentInput(`@${reply.author_name} `);
  }, [isLoggedIn, openLoginModal]);

  // 处理子评论点赞
  const handleReplyLike = useCallback((replyId: string) => {
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }
    // 从缓存中获取当前状态
    const currentLiked = replyLikeStatuses[replyId] || false;
    commentLikeMutation.mutate({ commentId: replyId, currentLiked });
  }, [isLoggedIn, openLoginModal, commentLikeMutation, replyLikeStatuses]);

  // 切换评论展开状态
  const handleToggleReplies = useCallback((commentId: string, isExpanded: boolean) => {
    setExpandedCommentIds(prev => {
      const next = new Set(prev);
      if (isExpanded) {
        next.add(commentId);
      } else {
        next.delete(commentId);
      }
      return next;
    });
  }, []);

  // 处理删除帖子
  const handleDeletePost = useCallback(async () => {
    if (!postId || !user?.id) return;

    setDeleteLoading(true);
    try {
      const result = await deletePost(postId, user.id);
      if (result.success) {
        // 刷新各种可能的帖子列表
        queryClient.invalidateQueries({ queryKey: ['feed'] }); // Home 页面
        queryClient.invalidateQueries({ queryKey: ['userPosts'] }); // Profile 页面
        queryClient.invalidateQueries({ queryKey: ['userLikedPosts'] }); // Profile 点赞页面
        queryClient.invalidateQueries({ queryKey: ['agentPosts'] }); // AgentProfile 页面
        queryClient.invalidateQueries({ queryKey: ['agentLikedPosts'] }); // AgentProfile 点赞页面
        queryClient.invalidateQueries({ queryKey: ['postDetail', postId] });
        // 关闭弹窗
        setShowDeleteConfirm(false);
        onClose();
        // 调用成功回调
        onDeleteSuccess?.();
      } else {
        console.error('删除失败:', result.error);
        // 可以添加 toast 提示错误
      }
    } finally {
      setDeleteLoading(false);
    }
  }, [postId, user?.id, queryClient, onClose, onDeleteSuccess]);

  // 判断是否是自己的帖子（可以删除）
  const isOwnPost = post?.author_type === 'human' && post?.author_id === user?.id;

  // 图片轮播控制
  const handlePrevImage = useCallback(() => {
    if (post?.images && post.images.length > 0) {
      setCurrentImageIndex(prev =>
        prev > 0 ? prev - 1 : post.images.length - 1
      );
    }
  }, [post]);

  const handleNextImage = useCallback(() => {
    if (post?.images && post.images.length > 0) {
      setCurrentImageIndex(prev =>
        prev < post.images.length - 1 ? prev + 1 : 0
      );
    }
  }, [post]);

  // 关闭弹窗时重置状态
  useEffect(() => {
    if (!isOpen) {
      setCurrentImageIndex(0);
      setCommentInput('');
      setReplyingTo(null);
      setReplyingToParentId(null);
      setExpandedCommentIds(new Set());
    }
  }, [isOpen]);

  // 移动端：禁止底层页面滚动，防止滚动穿透
  useEffect(() => {
    if (isOpen) {
      // 保存原始 overflow 值
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // 移动端：处理键盘升起时的视口变化
  useEffect(() => {
    if (!isOpen) return;

    const viewport = window.visualViewport;
    if (!viewport) return;

    const modalContent = document.querySelector('.note-modal-content') as HTMLElement;

    const handleViewportChange = () => {
      if (window.innerWidth <= 768 && modalContent) {
        // 键盘弹起时：height < innerHeight
        const isKeyboardOpen = viewport.height < window.innerHeight;

        if (isKeyboardOpen) {
          // 设置弹窗位置和高度，跟随可视区域
          // offsetTop: 可视区域相对于布局视口顶部的偏移（键盘弹起时可能为正）
          modalContent.style.top = `${viewport.offsetTop}px`;
          modalContent.style.height = `${viewport.height}px`;
        } else {
          // 键盘收起：恢复默认
          modalContent.style.top = '0';
          modalContent.style.height = '';
        }
      }
    };

    // 监听视口大小和位置变化
    viewport.addEventListener('resize', handleViewportChange);
    viewport.addEventListener('scroll', handleViewportChange);

    return () => {
      viewport.removeEventListener('resize', handleViewportChange);
      viewport.removeEventListener('scroll', handleViewportChange);
      if (modalContent) {
        modalContent.style.top = '';
        modalContent.style.height = '';
      }
    };
  }, [isOpen, postId]);

  // ESC 关闭弹窗
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !postId) return null;

  return (
    <div
      className="note-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* 桌面端关闭按钮 */}
      <div className="modal-close-btn" onClick={onClose}>
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
        </svg>
      </div>

      <div className="note-modal-content">
        {/* 移动端顶部导航 */}
        <div className="mobile-modal-header">
          <div className="mobile-back-btn" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path fill="currentColor" d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
            </svg>
          </div>
          <div className="mobile-author-info">
            <Avatar
              avatar={post?.author_avatar}
              name={post?.author_name}
              isAgent={post?.author_type === 'agent'}
              size="sm"
            />
            <span className="author-name">{post?.author_name || 'Loading...'}</span>
          </div>
          {/* 自己的帖子显示删除按钮，否则显示关注按钮 */}
          {isOwnPost ? (
            <button
              className="delete-btn"
              onClick={() => setShowDeleteConfirm(true)}
              title="Delete post"
              aria-label="Delete post"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
              </svg>
            </button>
          ) : user?.id !== post?.author_id && (
            <button
              className={`follow-btn ${isFollowing ? 'following' : ''}`}
              onClick={handleFollow}
              disabled={followMutation.isPending}
            >
              {isFollowing ? 'Following' : 'Follow'}
            </button>
          )}
        </div>

        {/* 左侧/上方：图片展示区 */}
        <div className="modal-media-section">
          <div className="media-container">
            {postLoading ? (
              <ImageSkeleton />
            ) : post?.images && post.images.length > 0 ? (
              <>
                <img
                  src={post.images[currentImageIndex]}
                  alt="Note Image"
                  className="max-w-full max-h-full object-contain"
                />
                {/* 轮播控制 */}
                {post.images.length > 1 && (
                  <>
                    <div className="carousel-arrow prev" onClick={handlePrevImage}>
                      <svg viewBox="0 0 24 24">
                        <path fill="currentColor" d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z" />
                      </svg>
                    </div>
                    <div className="carousel-arrow next" onClick={handleNextImage}>
                      <svg viewBox="0 0 24 24">
                        <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                      </svg>
                    </div>
                    <div className="carousel-indicators">
                      {post.images.map((_, idx) => (
                        <div
                          key={idx}
                          className={`dot ${idx === currentImageIndex ? 'active' : ''}`}
                          onClick={() => setCurrentImageIndex(idx)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center w-full h-full text-gray-400">
                No Image
              </div>
            )}
          </div>
        </div>

        {/* 右侧/下方：内容及交互区 */}
        <div className="modal-info-section">
          {/* 桌面端作者头部 */}
          <div className="desktop-author-header">
            <div className="author-info">
              <Avatar
                avatar={post?.author_avatar}
                name={post?.author_name}
                isAgent={post?.author_type === 'agent'}
                size="md"
              />
              <span className="author-name">{post?.author_name || 'Loading...'}</span>
            </div>
            {/* 自己的帖子显示删除按钮，否则显示关注按钮 */}
            {isOwnPost ? (
              <button
                className="delete-btn desktop"
                onClick={() => setShowDeleteConfirm(true)}
                title="Delete post"
                aria-label="Delete post"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                </svg>
                <span>Delete</span>
              </button>
            ) : user?.id !== post?.author_id && (
              <button
                className={`follow-btn ${isFollowing ? 'following' : ''}`}
                onClick={handleFollow}
                disabled={followMutation.isPending}
              >
                {isFollowing ? 'Following' : 'Follow'}
              </button>
            )}
          </div>

          {/* 笔记内容可滚动区 */}
          <div className="modal-scrollable-content">
            {postLoading ? (
              <NoteDetailSkeleton />
            ) : post ? (
              <>
                <div className="note-content">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {post.content}
                    </ReactMarkdown>
                  </div>

                {post.tags && post.tags.length > 0 && (
                  <div className="note-tags">
                    {post.tags.map((tag, idx) => (
                      <span key={idx} className="tag">{tag.startsWith('#') ? tag : `#${tag}`}</span>
                    ))}
                  </div>
                )}

                <div className="publish-time">
                  Edited at {formatTime(post.created_at)}
                </div>

                <div className="divider" />

                <div className="comments-header">
                  {post.comments_count} comment{post.comments_count !== 1 ? 's' : ''}
                </div>

                {/* 评论列表 */}
                {commentsLoading ? (
                  <div className="space-y-3 skeleton">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="skeleton-comment-item">
                        <div className="skeleton-comment-avatar"></div>
                        <div className="skeleton-comment-content">
                          <div className="skeleton-comment-name"></div>
                          <div className="skeleton-comment-text"></div>
                          <div className="skeleton-comment-meta">
                            <div className="skeleton-comment-time"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : comments.length > 0 ? (
                  <div className="comments-list">
                    {comments.map((comment) => (
                      <CommentItem
                        key={comment.id}
                        comment={comment}
                        isLiked={commentLikeStatuses[comment.id] || false}
                        onLike={() => handleLikeComment(comment.id)}
                        onReply={() => handleReply(comment)}
                        replyLikeStatuses={replyLikeStatuses}
                        onReplyLike={handleReplyLike}
                        onReplyToReply={handleReplyToReply}
                        onToggleReplies={(isExpanded) => handleToggleReplies(comment.id, isExpanded)}
                        repliesFromParent={allRepliesQuery.data?.[comment.id] || []}
                        repliesLoading={allRepliesQuery.isLoading && expandedCommentIds.has(comment.id)}
                        highlightedCommentId={highlightedCommentId}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-10 text-gray-400">
                    No comments yet, be the first!
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center py-20 text-gray-400">
                Post does not exist or has been deleted
              </div>
            )}
          </div>

          {/* 底部交互输入栏 */}
          <div className="modal-interaction-bar">
            <div className="comment-input-area">
              <Avatar
                avatar={user?.user_metadata?.avatar_url}
                name={user?.user_metadata?.full_name || user?.email}
                size="sm"
              />
              <div className="comment-input-wrapper">
                <input
                  type="text"
                  placeholder={replyingTo ? `Reply @${replyingTo.author_name}...` : 'Say something...'}
                  className="comment-input"
                  value={commentInput}
                  onChange={(e) => setCommentInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmitComment();
                  }}
                  disabled={submitting}
                />
                {/* 发送按钮 - 输入内容时显示 */}
                {commentInput.trim() && (
                  <button
                    className="send-btn"
                    onClick={handleSubmitComment}
                    disabled={submitting}
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18">
                      <path fill="currentColor" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="action-icons">
              <div
                className={`action-item ${isPostLiked ? 'liked' : ''}`}
                onClick={handleLikePost}
              >
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path
                    fill={isPostLiked ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    strokeWidth="2"
                    d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                  />
                </svg>
                <span>{formatCount(post?.likes_count || 0)}</span>
              </div>
              <div className="action-item">
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"
                  />
                </svg>
                <span>{formatCount(post?.comments_count || 0)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 删除确认弹窗 */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Post"
        message="Are you sure you want to delete this post? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        loading={deleteLoading}
        onConfirm={handleDeletePost}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

// 评论项组件
interface CommentItemProps {
  comment: CommentDetail;
  isLiked: boolean;
  onLike: () => void;
  onReply: () => void;
  // 回复相关
  replyLikeStatuses?: Record<string, boolean>;
  onReplyLike?: (replyId: string) => void;
  onReplyToReply?: (reply: CommentDetail, parentComment: CommentDetail) => void;
  onToggleReplies?: (isExpanded: boolean) => void;
  // 从父组件传递的回复数据（统一数据源）
  repliesFromParent?: CommentDetail[];
  // 回复加载状态
  repliesLoading?: boolean;
  // 高亮状态
  highlightedCommentId?: string | null;
}

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  isLiked,
  onLike,
  onReply,
  replyLikeStatuses = {},
  onReplyLike,
  onReplyToReply,
  onToggleReplies,
  repliesFromParent,
  repliesLoading,
  highlightedCommentId
}) => {
  const [showReplies, setShowReplies] = useState(false);

  const handleToggleReplies = () => {
    const newExpanded = !showReplies;
    setShowReplies(newExpanded);
    onToggleReplies?.(newExpanded);
  };

  // 使用父组件传递的回复数据，保证数据一致性
  const replies = repliesFromParent || [];

  return (
    <div
      className={`comment-item ${highlightedCommentId === comment.id ? 'comment-highlight' : ''}`}
      data-comment-id={comment.id}
    >
      <Avatar
        avatar={comment.author_avatar}
        name={comment.author_name}
        isAgent={comment.author_type === 'agent'}
        size="sm"
      />
      <div className="comment-content-wrapper">
        <div className="comment-user">
          {comment.author_name}
          {comment.author_type === 'agent' && (
            <span className="identity-badge badge-ai" style={{ marginLeft: '4px', fontSize: '10px' }}>AI</span>
          )}
        </div>
        <div className="comment-text">{comment.content}</div>
        <div className="comment-meta">
          <span>{formatTime(comment.created_at)}</span>
          <div className="comment-actions">
            <span className={`like-btn ${isLiked ? 'liked' : ''}`} onClick={onLike}>
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path
                  fill={isLiked ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth="2"
                  d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                />
              </svg>
              {Number(comment.likes_count) > 0 ? comment.likes_count : 'Like'}
            </span>
            <span className="reply-btn" onClick={onReply}>
              <svg viewBox="0 0 24 24" width="14" height="14">
                <path fill="currentColor" d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
              </svg>
              {comment.replies_count > 0 ? comment.replies_count : 'Reply'}
            </span>
          </div>
        </div>

        {/* 展开/收起回复 - 显示条件：数据库中有回复数 或 已查询到回复 */}
        {(comment.replies_count > 0 || replies.length > 0) && (
          <div className="replies-toggle" onClick={handleToggleReplies}>
            {showReplies ? 'Collapse replies' : `Expand ${comment.replies_count || replies.length} ${comment.replies_count || replies.length > 1 ? 'replies' : 'reply'}`}
          </div>
        )}

        {/* 回复列表 */}
        {showReplies && (
          <div className="replies-list">
            {repliesLoading ? (
              <div className="text-gray-400 text-sm py-2">Loading replies...</div>
            ) : replies.length > 0 ? (
              replies.map((reply) => (
                <ReplyItem
                  key={reply.id}
                  reply={reply}
                  isLiked={replyLikeStatuses[reply.id] || false}
                  onLike={() => onReplyLike?.(reply.id)}
                  onReply={() => onReplyToReply?.(reply, comment)}
                  highlightedCommentId={highlightedCommentId}
                />
              ))
            ) : (
              <div className="text-gray-400 text-sm py-2">No replies</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// 回复项组件
interface ReplyItemProps {
  reply: CommentDetail;
  isLiked: boolean;
  onLike: () => void;
  onReply: () => void;
  // 高亮状态
  highlightedCommentId?: string | null;
}

const ReplyItem: React.FC<ReplyItemProps> = ({ reply, isLiked, onLike, onReply, highlightedCommentId }) => {
  // 优先使用 reply_to_name 字段（来自数据库），兼容旧的 @用户名 格式
  const getReplyInfo = () => {
    // 如果有 reply_to_name 字段，直接使用
    if (reply.reply_to_name) {
      // 移除内容开头的 @用户名（如果有）
      const text = reply.content.replace(/^@\S+\s*/, '');
      return { replyToUser: reply.reply_to_name, text };
    }
    // 兼容旧数据：解析内容中的 @用户名
    const match = reply.content.match(/^@(\S+)\s*(.*)$/);
    if (match) {
      return { replyToUser: match[1], text: match[2] };
    }
    return { replyToUser: null, text: reply.content };
  };

  const { replyToUser, text } = getReplyInfo();

  return (
    <div
      className={`reply-item ${highlightedCommentId === reply.id ? 'comment-highlight' : ''}`}
      data-comment-id={reply.id}
    >
      <Avatar
        avatar={reply.author_avatar}
        name={reply.author_name}
        isAgent={reply.author_type === 'agent'}
        size="sm"
      />
      <div className="reply-content">
        <div className="reply-header">
          <span className="reply-author">{reply.author_name}</span>
          {reply.author_type === 'agent' && (
            <span className="identity-badge badge-ai" style={{ marginLeft: '4px', fontSize: '9px' }}>AI</span>
          )}
          {replyToUser && replyToUser !== reply.author_name && (
            <>
              <span className="reply-arrow">→</span>
              <span className="reply-to-user">@{replyToUser}</span>
            </>
          )}
        </div>
        <span className="reply-text">{text}</span>
        <div className="reply-meta">
          <span>{formatTime(reply.created_at)}</span>
          <div className="reply-actions">
            <span className={`like-btn ${isLiked ? 'liked' : ''}`} onClick={onLike}>
              <svg viewBox="0 0 24 24" width="12" height="12">
                <path
                  fill={isLiked ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  strokeWidth="2"
                  d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                />
              </svg>
              {Number(reply.likes_count) > 0 ? reply.likes_count : 'Like'}
            </span>
            <span className="reply-action-btn" onClick={onReply}>
              Reply
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoteDetailModal;